/**
 * Itinerary Section Skeleton Component
 * Loading state for the itinerary section showing:
 * - Pre-night accordion skeleton
 * - Pre-tour accordion skeleton
 * - 3 day card skeletons
 * - Post-tour accordion skeleton
 */

import React from "react";
import { Box, Stack, Skeleton } from "@mui/material";

/**
 * Skeleton for closed accordion (pre/post night or tour extensions)
 */
const AccordionSkeleton: React.FC = () => (
  <Box
    sx={{
      backgroundColor: "grey.95",
      borderRadius: "2rem",
      padding: "2.4rem",
      minHeight: "74px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: "1rem"
    }}
  >
    {/* Title skeleton */}
    <Skeleton variant="text" width="60%" height={28} sx={{ fontSize: "2rem" }} />
    {/* Expand icon skeleton */}
    <Skeleton variant="circular" width={24} height={24} />
  </Box>
);

/**
 * Skeleton for a single day card
 */
const DayCardSkeleton: React.FC = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", legacyDesktop: "row" },
      gap: "2.4rem",
      padding: { xs: "2.4rem 0", legacyTablet: "2.4rem 0", legacyDesktop: "2.4rem" }
    }}
  >
    {/* Image skeleton */}
    <Box
      sx={{
        width: { legacyDesktop: "45.6rem" },
        alignSelf: { xs: "stretch", legacyDesktop: "auto" },
        height: "30.7rem",
        borderRadius: "2rem",
        overflow: "hidden",
        flexShrink: 0
      }}
    >
      <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: "2rem" }} />
    </Box>

    {/* Content skeleton */}
    <Stack gap="1.2rem" flex={1}>
      {/* Title skeleton */}
      <Skeleton variant="text" width="70%" height={32} sx={{ fontSize: "2rem" }} />

      {/* Details skeleton (Accommodation & Meals) */}
      <Stack gap="0.8rem">
        <Skeleton variant="text" width="60%" height={24} sx={{ fontSize: "1.6rem" }} />
        <Skeleton variant="text" width="40%" height={24} sx={{ fontSize: "1.6rem" }} />
      </Stack>

      {/* Description skeleton (4 lines) */}
      <Stack gap="0.4rem">
        <Skeleton variant="text" width="100%" height={26} />
        <Skeleton variant="text" width="100%" height={26} />
        <Skeleton variant="text" width="100%" height={26} />
        <Skeleton variant="text" width="80%" height={26} />
      </Stack>
    </Stack>
  </Box>
);

const ItinerarySectionSkeleton: React.FC = () => (
  <Box
    sx={{
      width: "100%",
      padding: { xs: "unset", legacyTablet: "unset", legacyDesktop: "2rem" }
    }}
  >
    {/* Two-column layout: Timeline left (hidden on mobile), Content right */}
    <Box sx={{ display: "flex", gap: "2.4rem" }}>
      {/* Timeline Column Skeleton (hidden on mobile) */}
      <Box
        sx={{
          display: { xs: "none", legacyDesktop: "block" },
          width: "6.4rem",
          flexShrink: 0
        }}
      >
        {/* Timeline dots */}
        <Stack gap="20rem" sx={{ paddingTop: "3rem" }}>
          <Skeleton variant="circular" width={16} height={16} />
          <Skeleton variant="circular" width={16} height={16} />
          <Skeleton variant="circular" width={16} height={16} />
          <Skeleton variant="circular" width={16} height={16} />
          <Skeleton variant="circular" width={16} height={16} />
          <Skeleton variant="circular" width={16} height={16} />
          <Skeleton variant="circular" width={16} height={16} />
        </Stack>
      </Box>

      {/* Content Column */}
      <Box sx={{ flex: 1 }}>
        <Stack gap="0">
          {/* Pre-Night Accordion Skeleton */}
          <AccordionSkeleton />

          {/* Pre-Tour Accordion Skeleton */}
          <AccordionSkeleton />

          {/* Itinerary Header Skeleton */}
          <Box
            sx={{
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              padding: "2.4rem",
              marginTop: 0
            }}
          >
            <Skeleton variant="text" width="40%" height={32} sx={{ fontSize: "2rem" }} />
          </Box>

          {/* 3 Day Card Skeletons */}
          <DayCardSkeleton />
          <Box sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
            <DayCardSkeleton />
          </Box>
          <Box sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
            <DayCardSkeleton />
          </Box>
          <Box sx={{ padding: "2.4rem" }}>
            <Skeleton variant="rounded" width={200} height={40} sx={{ borderRadius: "20px" }} />
          </Box>
          {/* Post-Tour Accordion Skeleton */}
          <AccordionSkeleton />
          <AccordionSkeleton />
        </Stack>
      </Box>
    </Box>
  </Box>
);

export default ItinerarySectionSkeleton;
